import React, { useState } from "react";
import { Plus, Trash2, Save, FileText, User, MapPin } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";

const CreateQuotation = () => {
  const { token } = useAdminAuth();
  const [loading, setLoading] = useState(false);

  // --- STATE ---
  const [client, setClient] = useState({
    clientName: "",
    clientEmail: "",
    clientPhone: "",
    siteAddress: "",
    date: new Date().toISOString().split('T')[0]
  });

  const [sections, setSections] = useState([
    {
      sectionName: "First Floor / Kitchen",
      items: [{ description: "", quantity: 1, rate: 0 }]
    }
  ]);

  const [adjustment, setAdjustment] = useState(0);

  // --- HELPERS ---
  const handleClientChange = (e: any) => setClient({ ...client, [e.target.name]: e.target.value });

  const addSection = () => {
    setSections([...sections, { sectionName: "", items: [{ description: "", quantity: 1, rate: 0 }] }]);
  };

  const removeSection = (idx: number) => {
    const newSec = [...sections];
    newSec.splice(idx, 1);
    setSections(newSec);
  };

  const addItem = (secIdx: number) => {
    const newSec = [...sections];
    newSec[secIdx].items.push({ description: "", quantity: 1, rate: 0 });
    setSections(newSec);
  };

  const removeItem = (secIdx: number, itemIdx: number) => {
    const newSec = [...sections];
    newSec[secIdx].items.splice(itemIdx, 1);
    setSections(newSec);
  };

  const updateItem = (secIdx: number, itemIdx: number, field: string, value: any) => {
    const newSec = [...sections];
    newSec[secIdx].items[itemIdx] = { ...newSec[secIdx].items[itemIdx], [field]: value };
    setSections(newSec);
  };

  // Calculations
  const calculateTotal = () => {
    let total = 0;
    sections.forEach(sec => {
      sec.items.forEach(item => {
        total += (item.quantity * item.rate);
      });
    });
    return total;
  };

  // --- SUBMIT ---
  const handleSubmit = async () => {
    if(!client.clientName) return alert("Client Name is required");

    setLoading(true);
    const payload = {
      ...client,
      sections: sections,
      adjustment: Number(adjustment)
    };

    try {
      const res = await fetch("https://luxuriyahomes.com/api/quotations/create", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      
      if(res.ok) {
        alert("Quotation Saved & PDF Generated Successfully!");
        // Reset or Redirect
      } else {
        alert("Failed to create quotation");
      }
    } catch(e) {
      console.error(e);
      alert("Server Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen text-white font-[Serif] pb-20">
      
      <div className="flex justify-between items-center mb-8">
        <div>
           <h2 className="text-3xl font-light text-amber-500">Create New Quotation</h2>
           <p className="text-zinc-500 text-sm">Generate official estimates for clients</p>
        </div>
        <button 
          onClick={handleSubmit}
          disabled={loading}
          className="bg-amber-600 hover:bg-amber-500 text-black px-6 py-3 rounded font-bold uppercase tracking-widest flex items-center gap-2"
        >
          {loading ? "Generating..." : "Save & Generate PDF"} <FileText size={18}/>
        </button>
      </div>

      {/* CLIENT INFO CARD */}
      <div className="bg-[#1c1c1c] border border-zinc-800 p-6 rounded-xl mb-6 shadow-lg">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><User size={18} className="text-amber-500"/> Client Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <input name="clientName" placeholder="Client Name" className="bg-black border border-zinc-700 p-3 rounded text-white" onChange={handleClientChange} />
          <input name="clientPhone" placeholder="Phone Number" className="bg-black border border-zinc-700 p-3 rounded text-white" onChange={handleClientChange} />
          <input name="clientEmail" placeholder="Email Address" className="bg-black border border-zinc-700 p-3 rounded text-white" onChange={handleClientChange} />
          <input name="siteAddress" placeholder="Site Location" className="bg-black border border-zinc-700 p-3 rounded text-white" onChange={handleClientChange} />
        </div>
      </div>

      {/* SECTIONS & ITEMS */}
      <div className="space-y-6">
        {sections.map((section, secIdx) => (
          <div key={secIdx} className="bg-[#1c1c1c] border border-zinc-800 p-6 rounded-xl relative animate-in fade-in slide-in-from-bottom-4">
            
            {/* Remove Section Button */}
            {sections.length > 1 && (
               <button onClick={() => removeSection(secIdx)} className="absolute top-4 right-4 text-zinc-600 hover:text-red-500"><Trash2 size={18}/></button>
            )}

            {/* Section Title */}
            <div className="mb-4">
               <label className="text-xs uppercase text-zinc-500 font-bold">Section Name (e.g. Master Bedroom)</label>
               <input 
                 value={section.sectionName}
                 onChange={(e) => {
                    const newSec = [...sections];
                    newSec[secIdx].sectionName = e.target.value;
                    setSections(newSec);
                 }}
                 className="w-full bg-transparent text-xl font-bold text-white border-b border-zinc-700 focus:border-amber-500 outline-none py-2"
                 placeholder="Enter Section Name..."
               />
            </div>

            {/* Items Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-zinc-500 text-xs uppercase">
                    <th className="pb-2 w-[50%]">Description</th>
                    <th className="pb-2 w-[10%]">Qty</th>
                    <th className="pb-2 w-[15%]">Rate (₹)</th>
                    <th className="pb-2 w-[15%]">Amount</th>
                    <th className="pb-2 w-[5%]"></th>
                  </tr>
                </thead>
                <tbody className="space-y-2">
                  {section.items.map((item, itemIdx) => (
                    <tr key={itemIdx} className="group">
                      <td className="pr-2">
                        <textarea 
                          rows={2}
                          placeholder="Item description..."
                          className="w-full bg-black border border-zinc-800 p-2 rounded text-sm text-zinc-300 focus:border-amber-500 outline-none resize-none"
                          value={item.description}
                          onChange={(e) => updateItem(secIdx, itemIdx, "description", e.target.value)}
                        />
                      </td>
                      <td className="align-top pr-2">
                        <input 
                           type="number"
                           className="w-full bg-black border border-zinc-800 p-2 rounded text-sm text-center"
                           value={item.quantity}
                           onChange={(e) => updateItem(secIdx, itemIdx, "quantity", Number(e.target.value))}
                        />
                      </td>
                      <td className="align-top pr-2">
                        <input 
                           type="number"
                           className="w-full bg-black border border-zinc-800 p-2 rounded text-sm text-center"
                           value={item.rate}
                           onChange={(e) => updateItem(secIdx, itemIdx, "rate", Number(e.target.value))}
                        />
                      </td>
                      <td className="align-top py-2 font-bold text-amber-500">
                         ₹ {(item.quantity * item.rate).toLocaleString()}
                      </td>
                      <td className="align-top py-2 text-center">
                         <button onClick={() => removeItem(secIdx, itemIdx)} className="text-zinc-600 hover:text-red-500"><Trash2 size={16}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <button onClick={() => addItem(secIdx)} className="mt-4 text-xs font-bold uppercase text-amber-600 hover:text-amber-400 flex items-center gap-1">
              <Plus size={14}/> Add Item to {section.sectionName || "Section"}
            </button>

          </div>
        ))}

        {/* Add New Section Button */}
        <button 
          onClick={addSection}
          className="w-full py-4 border-2 border-dashed border-zinc-800 text-zinc-500 hover:border-amber-600 hover:text-amber-500 rounded-xl font-bold uppercase tracking-widest transition-all"
        >
          + Add New Section
        </button>

      </div>

      {/* SUMMARY FOOTER */}
      <div className="fixed bottom-0 right-0 w-full md:w-[calc(100%-16rem)] bg-[#161616] border-t border-zinc-800 p-6 flex flex-col md:flex-row justify-between items-center z-20 shadow-2xl">
         <div className="text-zinc-500 text-xs hidden md:block">
            * PDF will be saved automatically to server
         </div>
         
         <div className="flex items-center gap-8">
            <div className="text-right">
               <p className="text-zinc-400 text-xs uppercase">Sub Total</p>
               <p className="text-white font-bold">₹ {calculateTotal().toLocaleString()}</p>
            </div>
            <div className="text-right">
               <p className="text-zinc-400 text-xs uppercase">Adjustment (-)</p>
               <input 
                 type="number" 
                 className="bg-black border border-zinc-700 w-24 text-right px-2 py-1 rounded text-white text-sm"
                 value={adjustment}
                 onChange={(e) => setAdjustment(Number(e.target.value))}
               />
            </div>
            <div className="text-right pl-8 border-l border-zinc-700">
               <p className="text-amber-500 text-xs uppercase font-bold tracking-widest">Grand Total</p>
               <p className="text-2xl text-white font-light">₹ {(calculateTotal() - adjustment).toLocaleString()}</p>
            </div>
         </div>
      </div>

    </div>
  );
};

export default CreateQuotation;